// Gedeeld PDF-archief: kassier (eigen winkel, alleen bekijken) en beheerder (elke winkel, ook uploaden).
// Alle PDF's onder elkaar, nieuwste bovenaan, gewoon scrollen.
import { useCallback, useEffect, useRef, useState } from "react";
import { api, download, euro, foutTekst } from "../api";
import { PdfPaginas } from "./PdfDoorlopend";

const kb = (n) => (n > 1_000_000 ? `${(n / 1_000_000).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1000))} kB`);
const dagLang = (iso) => new Date(iso + "T12:00").toLocaleDateString("nl-BE", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
const maandNaam = (iso) => new Date(iso.slice(0, 7) + "-15T12:00").toLocaleDateString("nl-BE", { month: "long", year: "numeric" });
const knop = "bg-blue-600 text-white font-semibold rounded-xl px-4 py-2.5 text-sm";
const knopLicht = "bg-blue-50 text-blue-600 font-semibold rounded-xl px-3 py-2.5 text-sm";

// vid: alleen meegeven voor de beheerder (kassier -> backend kiest altijd de eigen winkel)
export default function PdfArchief({ vid, vestiging, van, tot, magUploaden = false, periodeKiezer = null }) {
  const [lijst, setLijst] = useState([]);
  const [eigen, setEigen] = useState(vestiging || null);
  const [soort, setSoort] = useState("binnengekomen");
  const [fout, setFout] = useState("");
  const [melding, setMelding] = useState("");
  const [bezig, setBezig] = useState(false);
  const [uploadDatum, setUploadDatum] = useState("");
  const invoer = useRef(null);
  const pdfFout = useCallback((e) => setFout(foutTekst(e)), []);
  const params = { van, tot, ...(vid ? { vestiging_id: vid } : {}) };

  const laad = useCallback(async () => {
    if (magUploaden && !vid) return;
    try {
      const r = await api.get("/pdf", { params: { van, tot, ...(vid ? { vestiging_id: vid } : {}) } });
      setLijst(r.data.items); setEigen(r.data.vestiging); setFout("");
    } catch (e) { setFout(foutTekst(e)); setLijst([]); }
  }, [vid, van, tot, magUploaden]);
  useEffect(() => { laad(); }, [laad]);

  const v = eigen || vestiging;
  const zichtbaar = lijst.filter((x) => soort === "alles" || x.soort === soort);

  const upload = async (files) => {
    if (!magUploaden || !files?.length) return;
    setBezig(true); setMelding(""); setFout("");
    const fd = new FormData();
    fd.append("vestiging_id", vid);
    if (uploadDatum) fd.append("datum", uploadDatum);
    [...files].forEach((f) => fd.append("bestanden", f));
    try {
      const r = (await api.post("/beheer/pdf", fd)).data;
      const nieuw = r.opgeslagen.filter((x) => !x.dubbel).length, dubbel = r.opgeslagen.length - nieuw;
      setMelding(`${nieuw} PDF('s) opgeslagen voor ${v?.korte_naam}${dubbel ? ` · ${dubbel} stond(en) er al in` : ""}`);
      if (r.fouten.length) setFout(r.fouten.map((x) => x.fout).join(" · "));
      laad();
    } catch (e) { setFout(foutTekst(e)); }
    setBezig(false);
    if (invoer.current) invoer.current.value = "";
  };

  let vorigeMaand = null;
  return (
    <div>
      {/* Balk blijft bovenaan staan tijdens het scrollen */}
      <div className="sticky top-0 z-30 bg-[#f1f4f9]/95 backdrop-blur px-5 pt-3 pb-2">
        <div className="bg-white rounded-2xl border border-slate-200 px-4 py-3 flex items-center gap-2 flex-wrap">
          <div className="mr-2">
            <h3 className="font-bold text-slate-800 leading-tight">{v?.naam}</h3>
            <div className="text-xs text-slate-500">{zichtbaar.length} PDF('s) · {van} – {tot} · nieuwste bovenaan</div>
          </div>
          {periodeKiezer}
          <div className="flex gap-1 text-xs">
            {[["binnengekomen", "Dagontvangsten (WW 3800)"], ["systeem", "Uit dagboek (Z)"], ["alles", "Alles"]].map(([k, l]) => (
              <button key={k} onClick={() => setSoort(k)}
                className={`px-3 py-1.5 rounded-lg font-semibold border ${soort === k ? "bg-[#1e2a44] text-white border-[#1e2a44]" : "bg-white text-slate-600 border-slate-200"}`}>{l}</button>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-2 flex-wrap">
            {magUploaden && (
              <>
                <label className="text-xs text-slate-500 flex items-center gap-1" title="Leeg laten = datum uit de bestandsnaam">datum
                  <input type="date" value={uploadDatum} onChange={(e) => setUploadDatum(e.target.value)} className="border rounded-lg px-2 py-1" />
                </label>
                <button className={knopLicht} disabled={bezig || !vid} onClick={() => invoer.current?.click()}>{bezig ? "Bezig…" : "⬆ PDF toevoegen"}</button>
                <input ref={invoer} type="file" accept="application/pdf,.pdf" multiple className="hidden" onChange={(e) => upload(e.target.files)} />
              </>
            )}
            <button className={knop} disabled={!lijst.length}
              onClick={() => download("/pdf-zip", params, `dagontvangsten-pdf_${v?.id || "winkel"}_${van}_${tot}.zip`).catch((e) => setFout(foutTekst(e)))}>⬇ Alles (ZIP)</button>
          </div>
        </div>
        {fout && <div className="mt-2 p-3 rounded-xl bg-red-50 text-red-700 text-sm">{fout}</div>}
        {melding && <div className="mt-2 p-3 rounded-xl bg-green-50 text-green-700 text-sm">✓ {melding}</div>}
      </div>

      {/* Alle PDF's onder elkaar – gewoon naar beneden scrollen */}
      <div className="px-5 pb-10 max-w-[1150px] mx-auto"
        onDragOver={(e) => magUploaden && e.preventDefault()} onDrop={(e) => { if (!magUploaden) return; e.preventDefault(); upload(e.dataTransfer.files); }}>
        {zichtbaar.length === 0 && (
          <div className="bg-white rounded-2xl border border-slate-200 text-slate-400 text-sm py-16 text-center mt-2">
            Geen PDF's voor {v?.korte_naam} in deze periode.
          </div>
        )}
        {zichtbaar.map((x) => {
          const maand = x.datum.slice(0, 7), kopje = maand !== vorigeMaand;
          vorigeMaand = maand;
          return (
            <div key={(v?.id || "") + x.id}>
              {kopje && <div className="text-sm font-extrabold text-blue-600 uppercase mt-5 mb-2">{maandNaam(x.datum)}</div>}
              <section className="bg-white rounded-2xl border border-slate-200 p-4 mb-5">
                <div className="flex items-center gap-3 mb-3 flex-wrap">
                  <span className={`w-11 h-11 shrink-0 rounded-lg font-extrabold text-[10px] flex items-center justify-center ${x.soort === "systeem" ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`}>
                    {x.soort === "systeem" ? `Z-${x.z_nummer}` : "PDF"}</span>
                  <div className="min-w-0">
                    <b className="text-slate-800 first-letter:uppercase block">{dagLang(x.datum)} · {v?.korte_naam}</b>
                    <div className="text-xs text-slate-500 truncate">
                      {x.soort === "systeem" ? `Digitaal dagboek (Z-${x.z_nummer}) · ${euro(x.totaal_eur)}`
                        : `${x.bestandsnaam} · ${kb(x.grootte)} · ontvangen ${x.ontvangen_op?.slice(0, 16).replace("T", " ")}${x.bron === "import" ? " (automatisch)" : ""} · ✓ ongewijzigd`}
                    </div>
                  </div>
                  <button className={`${knopLicht} ml-auto`}
                    onClick={() => download(`/pdf/${v?.id}/${x.id}`, { download: true }, x.bestandsnaam).catch((e) => setFout(foutTekst(e)))}>⬇ Download</button>
                </div>
                {v?.id && <PdfPaginas pad={`/pdf/${v.id}/${x.id}`} onFout={pdfFout} />}
              </section>
            </div>
          );
        })}
      </div>
    </div>
  );
}
